import ArticleIcon from "@mui/icons-material/Article";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import ListAltIcon from "@mui/icons-material/ListAlt";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import page3_section1 from "../../../assets/images/AboutVedaz/page3_section1.jpg";
import {
  boldFont,
  getRelativeFontSize,
  mediumFont,
  regularFont,
} from "../../../utils/styles";
import classes from "../AboutVedaz.styles";

interface Props {
  chapterIndex: number;
}

const features = [
  {
    icon: CompareArrowsIcon,
    color: "#9053F6",
    bg: "rgba(144,83,246,0.12)",
    border: "rgba(144,83,246,0.3)",
    rowAccent: "rgba(144,83,246,0.06)",
    titleKey: "about_func_f1_title",
    bodyKey: "about_func_f1_body",
  },
  {
    icon: ArticleIcon,
    color: "#5E75FF",
    bg: "rgba(94,117,255,0.12)",
    border: "rgba(94,117,255,0.3)",
    rowAccent: "rgba(94,117,255,0.06)",
    titleKey: "about_func_f2_title",
    bodyKey: "about_func_f2_body",
  },
  {
    icon: AutoAwesomeIcon,
    color: "#FFC107",
    bg: "rgba(255,193,7,0.1)",
    border: "rgba(255,193,7,0.3)",
    rowAccent: "rgba(255,193,7,0.05)",
    titleKey: "about_func_f3_title",
    bodyKey: "about_func_f3_body",
  },
  {
    icon: ListAltIcon,
    color: "#4CAF50",
    bg: "rgba(76,175,80,0.1)",
    border: "rgba(76,175,80,0.3)",
    rowAccent: "rgba(76,175,80,0.05)",
    titleKey: "about_func_f4_title",
    bodyKey: "about_func_f4_body",
  },
];

const AppFunctions = ({ chapterIndex }: Props) => {
  const { t } = useTranslation();

  return (
    <Box>
      {/* ── Section 1: Background image with glass card ───────────── */}
      <Box sx={classes.bgSection}>
        <Box
          component="img"
          src={page3_section1}
          alt=""
          sx={classes.bgSectionMedia}
        />
        <Box sx={classes.bgSectionOverlay} />
        <Box sx={classes.bgSectionContent}>
          <Typography sx={classes.sectionHeading}>
            {t("about_func_s1_heading")}
          </Typography>
          <Typography sx={{ ...classes.bodyText, maxWidth: "400px", mb: 0 }}>
            {t("about_func_s1_body")}
          </Typography>
        </Box>
      </Box>

      {/* ── Section 2: Feature spec rows ─────────────────────────── */}
      <Box sx={classes.plainSection}>
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            borderRadius: "16px",
            overflow: "hidden",
            border: "1px solid rgba(255,255,255,0.07)",
          }}
        >
          {features.map((f, i) => {
            const Icon = f.icon;
            const isLast = i === features.length - 1;
            return (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  alignItems: "stretch",
                  borderBottom: isLast
                    ? "none"
                    : "1px solid rgba(255,255,255,0.06)",
                  transition: "background 0.2s ease",
                  "&:hover": { background: f.rowAccent },
                }}
              >
                {/* Left: icon + title */}
                <Box
                  sx={{
                    width: { xs: "100%", sm: "38%" },
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: "14px",
                    px: { xs: "20px", sm: "28px" },
                    py: "22px",
                    borderRight: {
                      xs: "none",
                      sm: "1px solid rgba(255,255,255,0.06)",
                    },
                  }}
                >
                  {/* Colored icon badge */}
                  <Box
                    sx={{
                      width: "38px",
                      height: "38px",
                      borderRadius: "10px",
                      flexShrink: 0,
                      background: f.bg,
                      border: `1px solid ${f.border}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon sx={{ color: f.color, fontSize: "18px" }} />
                  </Box>

                  {/* Number + title stacked */}
                  <Box>
                    <Typography
                      sx={{
                        ...mediumFont,
                        fontSize: getRelativeFontSize(-4),
                        color: f.color,
                        lineHeight: 1,
                        mb: "4px",
                        opacity: 0.75,
                        letterSpacing: "0.5px",
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </Typography>
                    <Typography
                      sx={{
                        ...boldFont,
                        fontSize: getRelativeFontSize(2),
                        color: "#ffffff",
                        lineHeight: 1.25,
                      }}
                    >
                      {t(f.titleKey)}
                    </Typography>
                  </Box>
                </Box>

                {/* Right: body text */}
                <Box
                  sx={{
                    flex: 1,
                    display: { xs: "none", sm: "flex" },
                    alignItems: "center",
                    px: "28px",
                    py: "22px",
                  }}
                >
                  <Typography
                    sx={{
                      ...regularFont,
                      fontSize: getRelativeFontSize(0),
                      color: "rgba(255,255,255,0.52)",
                      lineHeight: 1.78,
                    }}
                  >
                    {t(f.bodyKey)}
                  </Typography>
                </Box>

                {/* Body visible on mobile below the title row */}
                <Box
                  sx={{
                    display: { xs: "block", sm: "none" },
                    px: "20px",
                    pb: "18px",
                  }}
                >
                  <Typography
                    sx={{
                      ...regularFont,
                      fontSize: getRelativeFontSize(0),
                      color: "rgba(255,255,255,0.52)",
                      lineHeight: 1.78,
                    }}
                  >
                    {t(f.bodyKey)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default AppFunctions;
